import { animate, stagger } from 'framer-motion/dom';
import { GameStateManager } from './state/GameStateManager.js';
import { selectEnemiesForDifficulty } from './scene/map/EnemySpawner.js';
import { DebugMenu } from './ui/DebugMenu.js';

// Server-backed auth state (source of truth is the session cookie, checked via
// /api/me). The in-game player name ({NAME}) is collected separately by the
// intro's NameInputScreen and is independent of this account email.
const AUTH = { loggedIn: false, email: null };

async function apiGet(url) {
    const r = await fetch(url, { credentials: 'same-origin' });
    return { ok: r.ok, body: await r.json().catch(() => ({})) };
}

async function apiPost(url, data) {
    const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(data),
    });
    return { ok: r.ok, body: await r.json().catch(() => ({})) };
}

async function refreshAuth() {
    const { ok, body } = await apiGet('/api/me');
    AUTH.loggedIn = ok;
    AUTH.email = ok ? body.email : null;
    updateButtons();
}

function navigateToGame(isNewRun = false) {
    document.body.classList.add('fade-out');

    setTimeout(() => {
        if (isNewRun) {
            window.location.href = '/game.html?newRun=true';
        } else {
            window.location.href = '/game.html';
        }
    }, 300);
}

function updateButtons() {
    const playBtn = document.getElementById('play-btn');
    const forgetBtn = document.getElementById('forget-btn');
    const loginBtn = document.getElementById('login-btn');
    const menuUsername = document.getElementById('menu-username');
    const hasExistingState = localStorage.getItem('air_game_save') !== null;
    
    if (!hasExistingState) {
        playBtn.textContent = 'NEW GAME';
        forgetBtn.style.display = 'none';
    } else {
        forgetBtn.style.display = '';
        const state = GameStateManager.load();
        if (state.run.isActive) {
            playBtn.textContent = 'CONTINUE';
            forgetBtn.textContent = 'TERMINATE';
        } else {
            playBtn.textContent = 'NEW RUN';
            forgetBtn.textContent = 'FORGET';
        }
    }

    const logoutBtn = document.getElementById('menu-logout-btn');

    menuUsername.textContent = AUTH.loggedIn ? AUTH.email : 'Guest';

    if (AUTH.loggedIn) {
        loginBtn.style.display = 'none';
        logoutBtn.style.display = '';
    } else {
        loginBtn.style.display = '';
        logoutBtn.style.display = 'none';
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const playBtn = document.getElementById('play-btn');
    const forgetBtn = document.getElementById('forget-btn');
    const optionsBtn = document.getElementById('options-btn');
    const optionsPanel = document.getElementById('options-panel');
    const optionsBackBtn = document.getElementById('options-back-btn');
    const hdrCheckbox = document.getElementById('hdr-checkbox');
    const musicCheckbox = document.getElementById('music-checkbox');

    // Auth elements
    const loginBtn = document.getElementById('login-btn');
    const loginPanel = document.getElementById('login-panel');
    const registerPanel = document.getElementById('register-panel');
    const loginBackBtn = document.getElementById('login-back-btn');
    const registerBackBtn = document.getElementById('register-back-btn');
    const loginSubmitBtn = document.getElementById('login-submit-btn');
    const registerSubmitBtn = document.getElementById('register-submit-btn');
    const loginEmail = document.getElementById('login-email');
    const loginError = document.getElementById('login-error');
    const loginRegisterLink = document.getElementById('login-register-link');
    const registerCode = document.getElementById('register-code');
    const registerEmail = document.getElementById('register-email');
    const registerError = document.getElementById('register-error');
    const registerLoginLink = document.getElementById('register-login-link');
    const menuLogoutBtn = document.getElementById('menu-logout-btn');

    updateButtons();
    // Sync UI to the real session, and surface magic-link verify errors (?error=).
    refreshAuth();
    const verifyError = new URLSearchParams(location.search).get('error');
    if (verifyError) {
        loginPanel.classList.add('visible');
        loginError.style.color = 'var(--color-coral)';
        loginError.textContent = verifyError === 'invalid_or_expired'
            ? 'That link was invalid or expired. Request a fresh one.'
            : 'Could not sign you in. Please try again.';
        history.replaceState({}, '', '/');
    }

    // Entrance animation: staggered fade-up with neon power-on flash
    const entranceElements = document.querySelectorAll('.menu-entrance');
    animate(entranceElements,
        {
            opacity: [0, 0.4, 1],
            y: [20, 0],
            filter: ['brightness(0.3)', 'brightness(2)', 'brightness(1)']
        },
        {
            duration: 0.8,
            delay: stagger(0.15, { startDelay: 0.3 }),
            easing: [0.22, 1, 0.36, 1]
        }
    );

    document.querySelector('h1').classList.add('shimmer-active');

    // Initialize checkboxes from saved settings
    hdrCheckbox.checked = GameStateManager.isHdrEnabled();
    musicCheckbox.checked = GameStateManager.isMusicEnabled();

    // Options panel handlers
    optionsBtn.addEventListener('click', () => {
        optionsPanel.classList.add('visible');
    });

    optionsBackBtn.addEventListener('click', () => {
        optionsPanel.classList.remove('visible');
    });

    hdrCheckbox.addEventListener('change', () => {
        GameStateManager.setHdrEnabled(hdrCheckbox.checked);
    });

    musicCheckbox.addEventListener('change', () => {
        GameStateManager.setMusicEnabled(musicCheckbox.checked);
    });

    function openLogin(message = '') {
        registerPanel.classList.remove('visible');
        loginPanel.classList.add('visible');
        loginEmail.value = '';
        loginError.style.color = message ? 'var(--color-cyan)' : 'var(--color-coral)';
        loginError.textContent = message;
        loginEmail.focus();
    }

    function openRegister() {
        loginPanel.classList.remove('visible');
        registerPanel.classList.add('visible');
        registerCode.value = '';
        registerEmail.value = '';
        registerError.style.color = 'var(--color-coral)';
        registerError.textContent = '';
        registerCode.focus();
    }

    // Login panel handlers — passwordless email magic link.
    loginBtn.addEventListener('click', () => openLogin());

    loginBackBtn.addEventListener('click', () => {
        loginPanel.classList.remove('visible');
    });

    loginSubmitBtn.addEventListener('click', async () => {
        const email = loginEmail.value.trim();
        if (!email) {
            loginError.style.color = 'var(--color-coral)';
            loginError.textContent = 'Please enter your email';
            return;
        }
        loginSubmitBtn.disabled = true;
        const { body } = await apiPost('/api/auth/request', { email });
        loginSubmitBtn.disabled = false;
        loginError.style.color = 'var(--color-cyan)';
        loginError.textContent = body.message || 'Check your email for a login link.';
    });

    loginEmail.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') loginSubmitBtn.click();
    });

    // Register link handler (from login panel) — go enter a license code.
    loginRegisterLink.addEventListener('click', () => openRegister());
    registerLoginLink.addEventListener('click', () => openLogin());

    registerBackBtn.addEventListener('click', () => {
        registerPanel.classList.remove('visible');
    });

    // Register panel handlers — redeem a single-use license code, which
    // registers the email and signs you in.
    registerSubmitBtn.addEventListener('click', async () => {
        const code = registerCode.value.trim();
        const email = registerEmail.value.trim();
        if (!code) {
            registerError.textContent = 'Please enter your license code';
            return;
        }
        if (!email) {
            registerError.textContent = 'Please enter your email';
            return;
        }
        registerSubmitBtn.disabled = true;
        const { ok, body } = await apiPost('/api/auth/redeem', { code, email });
        registerSubmitBtn.disabled = false;
        if (ok && body.redirect) {
            // Session cookie is set; reload so the menu reflects the new session.
            window.location.reload();
            return;
        }
        registerError.style.color = 'var(--color-coral)';
        registerError.textContent = body.error || 'Something went wrong.';
    });

    registerCode.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') registerEmail.focus();
    });

    registerEmail.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') registerSubmitBtn.click();
    });

    // Logout handler
    menuLogoutBtn.addEventListener('click', async () => {
        await apiPost('/api/auth/logout', {});
        window.location.reload();
    });

    const debugMenu = new DebugMenu();
    debugMenu.init();

    debugMenu.addButton('MAP', () => {
        document.body.classList.add('fade-out');
        setTimeout(() => {
            window.location.href = '/game.html?mode=map';
        }, 300);
    });

    debugMenu.addButton('RANDOM BATTLE', async () => {
        const difficulty = Math.floor(Math.random() * 3) + 1;
        const enemies = await selectEnemiesForDifficulty(difficulty);
        
        const battlePos = { q: 0, r: 1 };
        GameStateManager.setPendingBattle(battlePos.q, battlePos.r);
        GameStateManager.setTileEncounter(battlePos.q, battlePos.r, {
            original: enemies,
            remaining: enemies
        });
        
        document.body.classList.add('fade-out');
        setTimeout(() => {
            window.location.href = '/game.html?skipDialogue=true';
        }, 300);
    });

    // Play Button — requires a signed-in session (license code or login link).
    playBtn.addEventListener('click', () => {
        if (!AUTH.loggedIn) {
            openLogin('Please sign in to play.');
            return;
        }
        const state = GameStateManager.load();
        const isNewRun = !state.run.isActive;
        navigateToGame(isNewRun);
    });

    // Forget/Terminate Button
    forgetBtn.addEventListener('click', () => {
        const state = GameStateManager.load();
        const isTerminate = state.run.isActive;
        
        if (isTerminate) {
            GameStateManager.endRun();
            GameStateManager.save();
            forgetBtn.textContent = 'TERMINATED';
        } else {
            GameStateManager.reset();
            forgetBtn.textContent = 'FORGOTTEN';
        }
        
        updateButtons();
        
        setTimeout(() => {
            updateButtons();
        }, 500);
    });
});
